import { scaleLinear } from 'd3-scale';
import { Series, TooltipPoint } from '../../../interfaces/Charts';
import { DEFAULT_PADDING, BAR_GAP } from '../helpers/config';
import { useMemo } from 'react';
import { interpolateRoundToFixed } from '../helpers';
import { useNicedTicks } from './useNicedTicks';

const xAxisScale = (index: number) => BAR_GAP * index + DEFAULT_PADDING.left;

function transformData(matrix: Series[][]) {
  const groupedBars: Partial<TooltipPoint>[][] = [];
  let largestGroupSize = 1;
  // caching Array.length is faster. see http://bonsaiden.github.io/JavaScript-Garden/#array.general
  const groupsCount = matrix.length;

  let maxCalculated = 0;
  let minCalculated = 0;
  let maxLengthInLastGroup = 2;
  for (let groupIndex = 0; groupIndex < groupsCount; groupIndex++) {
    const group = matrix[groupIndex];
    const seriesCount = group.length;
    let maxLengthInGroup = 0;
    for (let seriesIndex = 0; seriesIndex < seriesCount; seriesIndex++) {
      const series = group[seriesIndex];
      const pointsCount = series.points.length;
      maxLengthInGroup = pointsCount > maxLengthInGroup ? pointsCount : maxLengthInGroup;
      for (let pointIndex = 0; pointIndex < pointsCount; pointIndex++) {
        const point = series.points[pointIndex];

        const plottedPoint = {
          index: pointIndex,
          x: xAxisScale(pointIndex + maxLengthInLastGroup),
          series: series,
        };
        // group points by their index
        let group = groupedBars[maxLengthInLastGroup + pointIndex];
        if (group) {
          group.push(plottedPoint);
        } else {
          groupedBars[maxLengthInLastGroup + pointIndex] = [plottedPoint];
          group = groupedBars[maxLengthInLastGroup + pointIndex];
        }
        largestGroupSize =
          group && group.length > largestGroupSize ? group.length : largestGroupSize;

        // calculate maximum and minimum of all the series
        maxCalculated = maxCalculated < point.value ? point.value : maxCalculated;
        minCalculated = minCalculated > point.value ? point.value : minCalculated;
      }
    }
    maxLengthInLastGroup += maxLengthInGroup;
  }
  return { largestGroupSize, groupedBars, minCalculated, maxCalculated };
}

const useOverlappedBars = (matrix: Series[][], height: number, yMax?: number, yMin?: number) => {
  const { maxCalculated, minCalculated, groupedBars, largestGroupSize } = useMemo(
    () => transformData(matrix),
    [matrix]
  );
  const max = yMax ? yMax : maxCalculated;
  const min = yMin ? yMin : minCalculated;

  const { ticks, nicedMin, nicedMax } = useNicedTicks(max, min);
  // Y-Axis -> ValueScale
  const valueScale = useMemo(() => {
    const graphBottom = height - DEFAULT_PADDING.top;
    const graphTop = DEFAULT_PADDING.bottom;
    const graphCenter = (graphBottom - graphTop) / 2;
    if (nicedMin === 0) {
      const valueScale = scaleLinear()
        .domain([0, nicedMax])
        .range([graphBottom, graphTop]);
      return valueScale;
    } else {
      const valueScale = scaleLinear<number>()
        .domain([nicedMin, 0, nicedMax])
        .range([graphBottom, graphCenter, graphTop])
        .interpolate(interpolateRoundToFixed(2));
      return valueScale;
    }
  }, [height, nicedMin, nicedMax]);
  // generate axis ticks
  return useMemo(() => {
    const plottedPoints = groupedBars.map(group =>
      group.map((point: TooltipPoint) => {
        point.y = valueScale(point.series.points[point.index].value);
        return point;
      })
    );

    return { plottedPoints, valueScale, largestGroupSize, ticks };
  }, [groupedBars, valueScale, largestGroupSize, ticks]);
};

export { useOverlappedBars };
